import React, { useState, useEffect } from 'react';
import EyeIcon from '../../icons/EyeIcon';
import DownloadIcon from '../../icons/DownloadIcon';
import TrashIcon from '../../icons/TrashIcon';
import FileUploadIcon from '../../icons/FileUploadIcon';
import PdfIcon from '../../icons/PdfIcon';
import DocxIcon from '../../icons/DocxIcon';
import ExcelIcon from '../../icons/ExcelIcon';
import PptIcon from '../../icons/PptIcon';
import FileIcon from '../../icons/FileIcon';
import SpinningLoader from '../../ui/SpinningLoader';
import Toast from '../../ui/Toast';
import DeleteModal from '../../modals/DeleteModal';
import { FileInfo } from '../../../types/fileRegistry';
import './DashboardDocumentsView.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.aspire.columbus.tech';
const FILES_ENDPOINT = `${API_BASE_URL}/api/v1/files`;

const DashboardDocumentsView: React.FC = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileInfo | null>(null);

  // Fetch files on mount
  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${FILES_ENDPOINT}/?scope=global&limit=100`);

      if (!response.ok) {
        throw new Error(`Failed to fetch files: ${response.status}`);
      }

      const data: FileInfo[] = await response.json();
      // Filter out deleted files
      const activeFiles = data.filter(file => file.status !== 'deleted');
      setFiles(activeFiles);
    } catch (error) {
      console.error('Error fetching files:', error);
      showToast('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('scope', 'global');

      const response = await fetch(`${FILES_ENDPOINT}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      showToast('Document uploaded successfully', 'success');
      setShowUploadModal(false);
      setSelectedFile(null);

      // Refresh the files list
      await fetchFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Failed to upload document', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleView = (file: FileInfo) => {
    // Open file in new tab
    const viewUrl = `${FILES_ENDPOINT}/${file.file_id}/download`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = async (file: FileInfo) => {
    try {
      setDownloadingFileId(file.file_id);
      const downloadUrl = `${FILES_ENDPOINT}/${file.file_id}/download`;
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast('Document downloaded', 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast('Failed to download document', 'error');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDeleteClick = (file: FileInfo) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    try {
      setDeletingFileId(fileToDelete.file_id);
      const response = await fetch(`${FILES_ENDPOINT}/${fileToDelete.file_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }

      showToast('Document deleted successfully', 'success');
      setShowDeleteModal(false);
      setFileToDelete(null);

      // Refresh the files list
      await fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast('Failed to delete document', 'error');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return <PdfIcon size={20} />;
      case 'doc':
      case 'docx':
        return <DocxIcon size={20} />;
      case 'xls':
      case 'xlsx':
        return <ExcelIcon size={20} />;
      case 'ppt':
      case 'pptx':
        return <PptIcon size={20} />;
      default:
        return <FileIcon size={20} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  return (
    <main className="dashboard-content documents-view">
      <header className="dashboard-header">
        <div className="header-top">
          <div className="breadcrumb" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <h1 className="main-title">Documents</h1>
              <button className="create-btn" onClick={() => setShowUploadModal(true)}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.4922 0.921875C13.888 1.31771 14.138 1.79167 14.2422 2.34375C14.3516 2.89062 14.4062 3.4974 14.4062 4.16406V10.2344C14.4062 10.9062 14.3516 11.5156 14.2422 12.0625C14.138 12.6094 13.888 13.0833 13.4922 13.4844C13.0911 13.8802 12.6146 14.1328 12.0625 14.2422C11.5104 14.3516 10.901 14.4062 10.2344 14.4062H4.16406C3.4974 14.4062 2.88802 14.3516 2.33594 14.2422C1.78906 14.1328 1.3151 13.8802 0.914062 13.4844C0.513021 13.0833 0.260417 12.6094 0.15625 12.0625C0.0520833 11.5156 0 10.9062 0 10.2344V4.14844C0 3.49219 0.0520833 2.89062 0.15625 2.34375C0.265625 1.79688 0.518229 1.32292 0.914062 0.921875C1.3151 0.526042 1.78906 0.273438 2.33594 0.164062C2.88802 0.0546875 3.49219 0 4.14844 0H10.2344C10.901 0 11.5104 0.0546875 12.0625 0.164062C12.6146 0.273438 13.0911 0.526042 13.4922 0.921875ZM13.1484 3.95312C13.1484 3.55208 13.112 3.15885 13.0391 2.77344C12.9661 2.38802 12.8099 2.07812 12.5703 1.84375C12.3359 1.60417 12.0234 1.44792 11.6328 1.375C11.2474 1.29688 10.8542 1.25781 10.4531 1.25781H3.97656C3.5651 1.25781 3.16146 1.29688 2.76562 1.375C2.375 1.44792 2.0625 1.60156 1.82812 1.83594C1.59375 2.07552 1.4401 2.39062 1.36719 2.78125C1.29427 3.16667 1.25781 3.5651 1.25781 3.97656V10.4531C1.25781 10.8542 1.29427 11.2474 1.36719 11.6328C1.4401 12.0182 1.59375 12.3281 1.82812 12.5625C2.06771 12.7969 2.38021 12.9531 2.76562 13.0312C3.15625 13.1094 3.55208 13.1484 3.95312 13.1484H10.4531C10.8542 13.1484 11.2474 13.1094 11.6328 13.0312C12.0234 12.9583 12.3359 12.8021 12.5703 12.5625C12.8047 12.3281 12.9583 12.0182 13.0312 11.6328C13.1094 11.2474 13.1484 10.8542 13.1484 10.4531V3.95312ZM3.58594 7.20312C3.58594 7.00521 3.64583 6.84635 3.76562 6.72656C3.89062 6.60156 4.05469 6.53906 4.25781 6.53906H6.55469V4.24219C6.55469 4.04427 6.61458 3.88281 6.73438 3.75781C6.85417 3.63281 7.00781 3.57031 7.19531 3.57031C7.39323 3.57031 7.55208 3.63281 7.67188 3.75781C7.79688 3.8776 7.85938 4.03906 7.85938 4.24219V6.53906H10.1641C10.362 6.53906 10.5208 6.60156 10.6406 6.72656C10.7656 6.84635 10.8281 7.00521 10.8281 7.20312C10.8281 7.39062 10.7656 7.54427 10.6406 7.66406C10.5208 7.78385 10.362 7.84375 10.1641 7.84375H7.85938V10.1484C7.85938 10.3464 7.79688 10.5078 7.67188 10.6328C7.55208 10.7526 7.39323 10.8125 7.19531 10.8125C7.00781 10.8125 6.85417 10.7526 6.73438 10.6328C6.61458 10.5078 6.55469 10.3464 6.55469 10.1484V7.84375H4.25781C4.0599 7.84375 3.89844 7.78385 3.77344 7.66406C3.64844 7.54427 3.58594 7.39062 3.58594 7.20312Z" fill="white" />
                </svg>
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="documents-table-container">
        {loading ? (
          <div className="loading-state">
            <SpinningLoader size="large" />
          </div>
        ) : files.length === 0 ? (
          <div className="empty-state">
            <FileUploadIcon size={64} className="empty-state-icon" />
            <h3 className="empty-state-title">No documents yet</h3>
            <p className="empty-state-description">
              Upload your first document to get started
            </p>
            <button className="create-btn" onClick={() => setShowUploadModal(true)}>
              Upload Document
            </button>
          </div>
        ) : (
          <table className="documents-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Size</th>
                <th>Date Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.file_id}>
                  <td>
                    <div className="file-name-cell">
                      <div className="file-icon-wrapper">
                        {getFileIcon(file.filename)}
                      </div>
                      <span className="file-name-text" title={file.filename}>
                        {file.filename}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="file-size-text">
                      {formatFileSize(file.file_size)}
                    </span>
                  </td>
                  <td>
                    <span className="file-date-text">
                      {formatDate(file.uploaded_at)}
                    </span>
                  </td>
                  <td>
                    <div className="file-actions">
                      <button
                        className="file-action-btn"
                        onClick={() => handleView(file)}
                        title="View document"
                      >
                        <EyeIcon />
                      </button>
                      <button
                        className="file-action-btn"
                        onClick={() => handleDownload(file)}
                        title="Download document"
                        disabled={downloadingFileId === file.file_id}
                      >
                        {downloadingFileId === file.file_id ? (
                          <SpinningLoader size="small" />
                        ) : (
                          <DownloadIcon />
                        )}
                      </button>
                      <button
                        className="file-action-btn delete"
                        onClick={() => handleDeleteClick(file)}
                        title="Delete document"
                        disabled={deletingFileId === file.file_id}
                      >
                        {deletingFileId === file.file_id ? (
                          <SpinningLoader size="small" />
                        ) : (
                          <TrashIcon size={14} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h2 className="upload-modal-title">Upload Document</h2>
              <button
                className="upload-modal-close"
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="upload-dropzone">
              <input
                type="file"
                id="file-input"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json"
              />
              <label htmlFor="file-input" style={{ cursor: 'pointer' }}>
                <FileUploadIcon size={48} className="upload-dropzone-icon" />
                <p className="upload-dropzone-text">
                  <strong>Click to upload</strong> or drag and drop
                </p>
                <p className="upload-dropzone-subtext">
                  PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, JSON
                </p>
              </label>
            </div>

            {selectedFile && (
              <div className="selected-file">
                <div className="file-icon-wrapper">
                  {getFileIcon(selectedFile.name)}
                </div>
                <div className="selected-file-info">
                  <div className="selected-file-name">{selectedFile.name}</div>
                  <div className="selected-file-size">{formatFileSize(selectedFile.size)}</div>
                </div>
                <button
                  className="selected-file-remove"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}

            <div className="upload-modal-actions">
              <button
                className="upload-modal-btn cancel"
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="upload-modal-btn upload"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={fileToDelete?.filename || ''}
        itemType="document"
        isDeleting={deletingFileId === fileToDelete?.file_id}
      />

      {/* Toast notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      />
    </main>
  );
};

export default DashboardDocumentsView;
